import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  resource,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiCoreModule } from '@softline/ui-core';
import { StampCaptureService } from './work-hours.service';
import { DateService, Store } from '@softline/core';
import { handleRequestError } from '@softline/application';
import { combineLatest, filter, timer } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TimestampData, Timestamp } from '../../data/timestamp.model';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';

@Component({
  selector: 'soft-work-hours-widget',
  templateUrl: './work-hours.widget.html',
  styleUrls: ['./work-hours.widget.scss'],
  imports: [CommonModule, UiCoreModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkHoursWidget {
  readonly #service = inject(StampCaptureService);
  readonly #injector = inject(Injector);
  readonly dateService = inject(DateService);
  store = inject(Store);

  context = this.store.signal(
    SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
    AuthenticationContextStore.getters.data
  );

  employeeId = computed(() => {
    return (this.context() as any)['personalid'];
  });

  _dayData = resource({
    request: () => ({ employeeId: this.employeeId() }),
    loader: async (request) => {
      try {
        const date = this.dateService.today();
        const data = await this.#service.retrieve(
          date.substr(0, 10),
          date.substr(0, 10),
          request.request.employeeId
        );
        return data;
      } catch (error) {
        handleRequestError(error, this.#injector, {
          show: true,
        });
      }
      return null;
    },
  });

  _weekData = resource({
    request: () => ({ employeeId: this.employeeId() }),
    loader: async (request) => {
      try {
        const date = this.dateService.today();
        const startOfWeek = this.dateService.weekStart(date);
        const data = await this.#service.retrieve(
          startOfWeek.substr(0, 10),
          date.substr(0, 10),
          request.request.employeeId
        );
        return data;
      } catch (error) {
        handleRequestError(error, this.#injector, {
          show: true,
        });
      }
      return null;
    },
  });

  weekData = computed(() => {
    return this._weekData.value() ?? undefined;
  });

  dayData = computed(() => {
    return this._dayData.value() ?? undefined;
  });

  private dayData$ = toObservable(this.dayData);

  now = signal(Date.now());

  constructor() {
    /*
    timer(0, 1000)
      .pipe(
        combineLatestWith(this.dayData$),
        map(([timer, data]) => [timer, data?.result[data?.result.length - 1]?.flag]),
        filter(([_, flag]) => flag === 'arrival'),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.now.set(Date.now());
      });
      */
    combineLatest([timer(0, 1000), toObservable(this.#service.isStamped)])
      .pipe(
        takeUntilDestroyed(),
        filter(([_, isStamped]) => isStamped)
      )
      .subscribe(() => {
        this.now.set(Date.now());
      });
  }

  #calculateWorkHours = (entries: Timestamp[] | undefined) => {
    if (!entries) return;
    const currentTime = this.now(); // will trigger updates
    let totalMilliseconds = 0;
    let currentArrival: number | null = null;
    for (const entry of entries) {
      const time = new Date(entry.timestamp).getTime();

      if (entry.flag === 'arrival') {
        currentArrival = time;
      } else if (entry.flag === 'departure' && currentArrival !== null) {
        totalMilliseconds += time - currentArrival;
        currentArrival = null;
      }
    }
    if (currentArrival !== null) {
      totalMilliseconds += currentTime - currentArrival;
    }
    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    return {
      hours: this.pad(hours),
      minutes: this.pad(minutes),
      seconds: this.pad(seconds),
    };
  };

  #calculatePauseTime(entries: Timestamp[] = []): {
    hours: string;
    minutes: string;
    seconds: string;
  } {
    let totalMilliseconds = 0;
    let currentDeparture: number | null = null;

    for (const entry of entries) {
      const time = new Date(entry.timestamp).getTime();

      if (entry.flag === 'departure') {
        currentDeparture = time;
      } else if (entry.flag === 'arrival' && currentDeparture !== null) {
        // Time between departure → arrival is pause
        totalMilliseconds += time - currentDeparture;
        currentDeparture = null;
      }
    }

    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    return {
      hours: this.pad(hours),
      minutes: this.pad(minutes),
      seconds: this.pad(seconds),
    };
  }

  sortByDate(a: Timestamp, b: Timestamp) {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    if (dateA < dateB) {
      return -1;
    }
    if (dateA > dateB) {
      return 1;
    }
    return 0;
  }

  sameDay(d1: string, d2: string) {
    const date1 = new Date(d1);
    const date2 = new Date(d2);
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  sameWeek(d1: string, d2: string) {
    const startOfWeek1 = new Date(this.dateService.weekStart(d1));
    const startOfWeek2 = new Date(this.dateService.weekStart(d2));
    return (
      startOfWeek1.getFullYear() === startOfWeek2.getFullYear() &&
      startOfWeek1.getMonth() === startOfWeek2.getMonth() &&
      startOfWeek1.getDate() === startOfWeek2.getDate()
    );
  }

  dayWorkHours = computed(() => {
    const data = this.dayData() as TimestampData;
    if (!data) return;
    // filter out other dates
    const date = this.dateService.today();
    data.result = data.result.filter((entry: Timestamp) => {
      return this.sameDay(entry.timestamp, date);
    });
    // sort by date;
    data.result = data.result.sort((a: Timestamp, b: Timestamp) => {
      return this.sortByDate(a, b);
    });
    return this.#calculateWorkHours(data.result as Timestamp[]);
  });

  weekWorkHours = computed(() => {
    const data = this.weekData() as TimestampData;
    if (!data) return;
    const date = this.dateService.today();
    // filter out other dates
    data.result = data.result.filter((entry: Timestamp) => {
      return this.sameWeek(entry.timestamp, date);
    });

    // sort by date
    data.result = data.result.sort((a: Timestamp, b: Timestamp) => {
      return this.sortByDate(a, b);
    });


    return this.#calculateWorkHours(data.result as Timestamp[]);
  });

  pauseTime = computed(() => {
    const data = this.dayData() as TimestampData;
    if (!data) return;
    return this.#calculatePauseTime(data.result as Timestamp[]);
  });

  pad(n: number | undefined | null): string {
    if (n === undefined || n === null) return '00';
    return n.toString().padStart(2, '0');
  }
}
